import { SESSION_HALF_HOUR_SLOTS } from '@src/components/sco/Advisors/utils/sessionFormat'

export const SESSION_WEEKDAYS = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' },
] as const

export type SessionWeekday = (typeof SESSION_WEEKDAYS)[number]['key']

export type SessionWeeklyHour = {
  weekday: SessionWeekday
  start: string
  end: string
}

export type SessionDayHours = {
  weekday: SessionWeekday
  enabled: boolean
  start: string
  end: string
}

export const DEFAULT_DAY_START = '09:00'
export const DEFAULT_DAY_END = '17:00'

/** Full local day, 30-minute steps. Matches session booking slots. */
export const SESSION_HOUR_SLOTS = SESSION_HALF_HOUR_SLOTS

const WEEKDAY_KEYS = new Set(SESSION_WEEKDAYS.map(day => day.key))

const isWeekday = (value: string): value is SessionWeekday => WEEKDAY_KEYS.has(value as SessionWeekday)

const toMinutes = (hhmm: string) => {
  const [hour, minute] = hhmm.split(':').map(Number)
  return hour * 60 + minute
}

/** `null` when nothing has been saved yet (caller should apply weekday defaults). */
export const parseSessionWeeklyHours = (raw: unknown): SessionWeeklyHour[] | null => {
  if (raw == null) {
    return null
  }
  if (!Array.isArray(raw)) {
    return []
  }
  const seen = new Set<string>()
  const hours: SessionWeeklyHour[] = []
  for (const row of raw) {
    if (!row || typeof row !== 'object') {
      continue
    }
    const weekday = String((row as SessionWeeklyHour).weekday || '')
    const start = String((row as SessionWeeklyHour).start || '')
    const end = String((row as SessionWeeklyHour).end || '')
    if (!isWeekday(weekday) || seen.has(weekday)) {
      continue
    }
    if (!SESSION_HOUR_SLOTS.some(slot => slot.value === start)) {
      continue
    }
    if (!SESSION_HOUR_SLOTS.some(slot => slot.value === end)) {
      continue
    }
    if (toMinutes(end) <= toMinutes(start)) {
      continue
    }
    seen.add(weekday)
    hours.push({ weekday, start, end })
  }
  return hours
}

/** UI rows: saved hours, all-off when an empty list was saved, or Mon–Fri 9–5 when unset. */
export const weeklyHoursToDayRows = (hours: SessionWeeklyHour[] | null): SessionDayHours[] => {
  const hasSaved = hours != null
  const list = hours ?? []
  const byDay = new Map(list.map(hour => [hour.weekday, hour]))
  return SESSION_WEEKDAYS.map(day => {
    const saved = byDay.get(day.key)
    if (saved) {
      return { weekday: day.key, enabled: true, start: saved.start, end: saved.end }
    }
    const weekdayDefault = hasSaved ? false : day.key !== 'sat' && day.key !== 'sun'
    return {
      weekday: day.key,
      enabled: weekdayDefault,
      start: DEFAULT_DAY_START,
      end: DEFAULT_DAY_END,
    }
  })
}

export const dayRowsToWeeklyHours = (rows: SessionDayHours[]): SessionWeeklyHour[] =>
  rows.filter(row => row.enabled).map(row => ({ weekday: row.weekday, start: row.start, end: row.end }))

const weekdayLabel = (key: SessionWeekday) => SESSION_WEEKDAYS.find(day => day.key === key)?.label || key

const clockLabel = (hhmm: string) => SESSION_HOUR_SLOTS.find(slot => slot.value === hhmm)?.label || hhmm

/**
 * Founder-facing summary of days/hours the expert prefers.
 * Returns null when they have not indicated any window — callers must not treat
 * this as a booking constraint.
 */
export const formatSessionWeeklyHoursPreference = (
  hours: SessionWeeklyHour[] | null | undefined
): string | null => {
  if (!hours?.length) {
    return null
  }
  const order = SESSION_WEEKDAYS.map(day => day.key)
  const sorted = [...hours].sort((a, b) => order.indexOf(a.weekday) - order.indexOf(b.weekday))
  const groups: { start: SessionWeekday; end: SessionWeekday; startTime: string; endTime: string }[] = []
  for (const hour of sorted) {
    const last = groups[groups.length - 1]
    const prevIndex = last ? order.indexOf(last.end) : -1
    const thisIndex = order.indexOf(hour.weekday)
    if (last && thisIndex === prevIndex + 1 && last.startTime === hour.start && last.endTime === hour.end) {
      last.end = hour.weekday
      continue
    }
    groups.push({
      start: hour.weekday,
      end: hour.weekday,
      startTime: hour.start,
      endTime: hour.end,
    })
  }
  return groups
    .map(group => {
      const days =
        group.start === group.end
          ? weekdayLabel(group.start)
          : `${weekdayLabel(group.start)}–${weekdayLabel(group.end)}`
      return `${days}, ${clockLabel(group.startTime)}–${clockLabel(group.endTime)}`
    })
    .join(' · ')
}
